import React from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '@/hooks/useAuth';
import cn from '@/utils/cn';
import { homePage, sideNav } from '@/utils/translations';
import { HorizontalSeparator } from '@/components/Separator';
import useXero from '@/hooks/useXero';
import useHomeContext from '@/hooks/useHomeContext';
import { LogoutIcon, PasskeyIcon } from '@/components/Icons';
import { useProfile } from '@/hooks/useProfile';
import { routes } from '@/utils/routes';

interface UserSettingsDropdownMenuProps {
  showNavBarMenu?: string;
  onCloseMenu: () => void;
}

interface MenuOption {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
}

const ContainerClass = cn([
  'bg-white',
  'text-emerge-navy',
  'absolute',
  'z-50',
  'right-0',
  'top-full',
  'mt-2',
  'overflow-hidden',
  'flex',
  'flex-col',
  'w-[224px]',
  'rounded-[6px]',
  'shadow-[0px_1px_2px_rgba(0,0,0,0.08)]'
]);

const UserSettingsDropdownMenu: React.FC<UserSettingsDropdownMenuProps> = ({
  showNavBarMenu,
  onCloseMenu
}) => {
  const authContext = useAuth()!;
  const xeroContext = useXero();
  const homeContext = useHomeContext();
  const navigate = useNavigate();

  const profile = useProfile();

  const handleLogout = () => {
    authContext?.onLogout();
    xeroContext?.clearXeroContext();
    homeContext?.clearHomeContext();
  };

  const menuOptions: MenuOption[] = [
    {
      label: sideNav.passkeys,
      icon: <PasskeyIcon />,
      onClick: () => {
        onCloseMenu();
        navigate(routes.passkeySettings);
      }
    },
    {
      label: homePage.logout,
      icon: <LogoutIcon />,
      onClick: handleLogout
    }
  ];

  return (
    <>
      {showNavBarMenu === 'usersettings' ? (
        <div className={ContainerClass} onMouseLeave={onCloseMenu}>
          {/* User name + access level */}
          <div className="inline-flex flex-col items-start justify-start px-4 py-3">
            <span className="break-words text-sm font-medium leading-5 text-emerge-navy">
              {profile.name}
            </span>
            <span className="break-words text-xs font-medium text-emerge-navy-2">
              {profile.accessLevel}
            </span>
          </div>

          {/* Divider */}
          <HorizontalSeparator />

          {/* Menu options */}
          {menuOptions.map((option, idx) => (
            <React.Fragment key={`user-settings-option-${idx}`}>
              {idx > 0 ? <HorizontalSeparator /> : null}
              <div className="py-[2px] hover:bg-emerge-base-2" onClick={option.onClick}>
                <div className="flex cursor-pointer items-center gap-2 px-4 py-2">
                  {option.icon}
                  <div className="text-xs font-medium text-emerge-navy">{option.label}</div>
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      ) : null}
    </>
  );
};

export default UserSettingsDropdownMenu;
